import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../lib/assets";
import { Wordmark } from "./Wordmark";

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative font-sans text-[13px] tracking-[0.18em] uppercase transition-colors duration-500 ${
      isActive ? "text-aveden-gold" : "text-aveden-gray hover:text-aveden-white"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-aveden-black/85 backdrop-blur-md border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 relative">
        {!scrolled && (
          <div className="pointer-events-none absolute inset-x-0 -top-4 h-44 bg-gradient-to-b from-aveden-black/90 via-aveden-black/55 to-transparent" />
        )}
        <div className="relative flex items-center justify-between h-24 lg:h-28">
          <div className="flex items-center gap-10 lg:gap-16">
            <Wordmark size="md" testId="nav-wordmark" />

            <div className="hidden md:flex items-center gap-10 lg:gap-14">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.to} className="relative group">
                  <NavLink
                    to={link.to}
                    data-testid={`nav-link-${slug(link.label)}`}
                    className={linkClass}
                  >
                    {({ isActive }) => (
                      <span className="inline-block pb-1">
                        {link.label}
                        <ChevronDown
                          size={13}
                          strokeWidth={1.5}
                          className="inline-block align-middle ml-1.5 -mt-0.5 transition-transform duration-500 group-hover:rotate-180"
                        />
                        <span
                          className={`block h-px bg-aveden-gold transition-all duration-500 mt-1 ${
                            isActive ? "w-full" : "w-0"
                          }`}
                        />
                      </span>
                    )}
                  </NavLink>
                  <div className="absolute left-0 top-full pt-5 opacity-0 invisible -translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-500 ease-out">
                    <div
                      data-testid={`nav-dropdown-${slug(link.label)}`}
                      className="min-w-[244px] bg-aveden-black/95 backdrop-blur-xl border border-white/[0.08] shadow-2xl shadow-black/60"
                    >
                      <div className="h-px w-full bg-gradient-to-r from-aveden-gold/70 via-aveden-gold/20 to-transparent" />
                      <div className="py-2.5">
                        {link.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            data-testid={`nav-link-${slug(child.label)}`}
                            className={({ isActive }) =>
                              `group/item flex items-center gap-3 px-6 py-3.5 font-sans text-[12px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                                isActive
                                  ? "text-aveden-gold"
                                  : "text-aveden-gray hover:text-aveden-white"
                              }`
                            }
                          >
                            <span className="h-px w-0 bg-aveden-gold transition-all duration-300 group-hover/item:w-4" />
                            <span>{child.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${slug(link.label)}`}
                  className={linkClass}
                >
                  {({ isActive }) => (
                    <span className="inline-block pb-1">
                      {link.label}
                      <span
                        className={`block h-px bg-aveden-gold transition-all duration-500 mt-1 ${
                          isActive ? "w-full" : "w-0"
                        }`}
                      />
                    </span>
                  )}
                </NavLink>
              )
            )}
          </div>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-aveden-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden bg-aveden-black/95 backdrop-blur-md transition-all duration-500 ${
          open ? "max-h-[560px] border-b border-white/[0.06]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col items-start">
          <div className="flex flex-col items-start text-left gap-6">
            {NAV_LINKS.map((link) => (
              <div key={link.to} className="flex flex-col items-start gap-4">
                <NavLink
                  to={link.to}
                  data-testid={`mobile-nav-link-${slug(link.label)}`}
                  className={({ isActive }) =>
                    `font-sans text-sm tracking-[0.18em] uppercase ${
                      isActive ? "text-aveden-gold" : "text-aveden-gray"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
                {link.children && (
                  <div className="flex flex-col items-start gap-4 pl-5 border-l border-white/[0.08]">
                    {link.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={`mobile-nav-link-${slug(child.label)}`}
                        className={({ isActive }) =>
                          `font-sans text-[13px] tracking-[0.16em] uppercase ${
                            isActive ? "text-aveden-gold" : "text-aveden-gray/80"
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
